import {
	CloseOutlined,
	PlusOutlined,
	ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	Button,
	Divider,
	Dropdown,
	Flex,
	Form,
	Input,
	Modal,
	Radio,
	Select,
	Switch,
	Tag,
	message,
	theme,
} from "antd";
import { Option } from "antd/es/mentions";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { TapAnimationComponent } from "components";
import { motion } from "framer-motion";
import { some } from "lodash";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const { TextArea } = Input;

const StyledSwitch = styled(Switch)`
	&.ant-switch-checked {
		background-color: ${COMMONS.CUSTOM_RED};
	}

	&.ant-switch-checked:focus {
		-webkit-box-shadow: 0 0 0 2px ${COMMONS.CUSTOM_LIGHT_RED};
		box-shadow: 0 0 0 2px ${COMMONS.CUSTOM_LIGHT_RED};
	}
`;

const FormItem = styled(Form.Item)`
	margin-bottom: 0px;
`;

const CustomerRegistrationModal = (props) => {
	const colorPrimary = theme.useToken().token.colorPrimary;
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		isCustomerRegistrationModalVisible,
		hideCustomerRegistrationModal,
		currentCustomerRegistration,
	} = props;
	const [customerRegistrationForm] = Form.useForm();
	const [type, setType] = useState();
	const [oldChoices, setOldChoices] = useState();

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();

	useEffect(() => {
		if (
			isCustomerRegistrationModalVisible &&
			currentCustomerRegistration?.customerRegistrationId
		) {
			setType(currentCustomerRegistration?.type);
			const currentCampaignChoices =
				currentCustomerRegistration?.campaignChoices
					? currentCustomerRegistration.campaignChoices.map((choice) => ({
							contents: choice?.contents,
							isDelete: choice?.isDelete,
							type: choice?.type,
							campaignChoiceId: choice?.campaignChoiceId,
						}))
					: [{}];
			setOldChoices(currentCampaignChoices);
			customerRegistrationForm.setFieldsValue({
				required: currentCustomerRegistration?.required,
				label: currentCustomerRegistration?.label,
				type: currentCustomerRegistration?.type,
				isDisplayed: `${currentCustomerRegistration?.isDisplayed}`,
				choices: currentCampaignChoices,
				isAdminDisplayed: `${currentCustomerRegistration?.isAdminDisplayed}`,
			});
		}

		// eslint-disable-next-line
	}, [currentCustomerRegistration, isCustomerRegistrationModalVisible]);

	const createCustomerRegistrationMutation = useMutation(
		API.ADMIN_CREATE_CUSTOMER_REGISTRATION,
		{
			onSuccess: (response) => {
				if (isMountedRef.current) {
					message.success(COMMONS.SUCCESS_CREATE_MSG);
					queryClient.invalidateQueries({
						queryKey: [API.QUERY_KEY_ADMIN_CUSTOMER_REGISTRATIONS],
					});
					hideCustomerRegistrationModal();
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	const updateCustomerRegistrationMutation = useMutation(
		API.ADMIN_UPDATE_CUSTOMER_REGISTRATION,
		{
			onSuccess: (response) => {
				if (isMountedRef.current) {
					message.success(COMMONS.SUCCESS_UPDATE_MSG);
					queryClient.invalidateQueries({
						queryKey: [API.QUERY_KEY_ADMIN_CUSTOMER_REGISTRATIONS],
					});
					hideCustomerRegistrationModal();
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	const showConfirmationModal = ({ values, choices }) => {
		Modal.confirm({
			title: "確認",
			icon: <ExclamationCircleOutlined className="text-red-600" />,
			content: (
				<p>
					編集すると登録済みデータも含め、全て上書きされます。よろしいですか？
				</p>
			),
			okText: "確認",
			okButtonProps: {
				type: "primary",
				danger: true,
			},
			cancelText: "キャンセル",
			centered: true,
			onOk() {
				updateCustomerRegistrationMutation.mutate({
					...values,
					choices,
					customerRegistrationId:
						currentCustomerRegistration?.customerRegistrationId,
				});
				customerRegistrationForm.resetFields();
				setType("text");
			},
		});
	};

	const handleCreateCustomerRegistration = (values) => {
		if (
			COMMONS.isTrue(
				values[COMMONS.SETTINGS_CUSTOMER_REGISTRATION_TYPE.isDisplayed],
			) &&
			!COMMONS.isTrue(
				values[COMMONS.SETTINGS_CUSTOMER_REGISTRATION_TYPE.isAdminDisplayed],
			)
		) {
			message.error(
				COMMONS.ERROR_WRONG_SETTING_DISPLAY_CUSTOM_REGISTRATION_MSG,
			);
			return;
		}
		const choices = values?.choices
			? values.choices.map((choice, i) => ({
					contents: choice?.contents,
					showOrder: i,
					type: choice.type,
					campaignChoiceId: choice?.campaignChoiceId,
				}))
			: [];

		if (currentCustomerRegistration?.customerRegistrationId) {
			const isConfirmEdit = COMMONS.checkCampaignChoicesNotDeleteChanged({
				newChoices: values?.choices,
				oldChoices,
			});
			if (currentCustomerRegistration?.isZipCode) {
				values.isZipCode = currentCustomerRegistration?.isZipCode;
			}
			if (currentCustomerRegistration?.isAddress) {
				values.isAddress = currentCustomerRegistration?.isAddress;
			}
			if (isConfirmEdit) {
				return showConfirmationModal({ values, choices });
			} else {
				updateCustomerRegistrationMutation.mutate({
					...values,
					choices,
					customerRegistrationId:
						currentCustomerRegistration?.customerRegistrationId,
				});
			}
		} else {
			createCustomerRegistrationMutation.mutate({ ...values, choices });
		}
		customerRegistrationForm.resetFields();
		setType("text");
	};

	const onChoicesDragEnd = (result) => {
		// dropped outside the list
		if (!result.destination) {
			return;
		} else {
			const orderedChoices = COMMONS.RE_ORDER(
				customerRegistrationForm.getFieldValue("choices"),
				result.source.index,
				result.destination.index,
			);
			customerRegistrationForm.setFieldsValue({ choices: orderedChoices });
		}
	};

	return (
		<>
			<Modal
				open={isCustomerRegistrationModalVisible}
				onCancel={() => {
					hideCustomerRegistrationModal();
					customerRegistrationForm.resetFields();
					setType("text");
				}}
				title={"項目登録追加"}
				footer={null}
				styles={{
					body: {
						maxHeight: "90vh",
						overflowY: "auto",
						overflowX: "hidden",
					},
				}}
				maskClosable={false}
				width={720}
				destroyOnClose
				centered
			>
				<Form
					form={customerRegistrationForm}
					layout="vertical"
					onFinish={handleCreateCustomerRegistration}
					size="large"
					requiredMark={false}
					scrollToFirstError={COMMONS.FORM_SCROLL_CONFIG}
					initialValues={{
						required: false,
						label: "",
						type: "text",
						choices: [{}],
					}}
				>
					<motion.div
						variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
						initial="hidden"
						animate="show"
						exit="hidden"
					>
						<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
							<Flex gap={10} align="center">
								必須設定
								<FormItem name="required" valuePropName="checked">
									<StyledSwitch />
								</FormItem>
							</Flex>
						</motion.div>

						<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
							<Form.Item
								name="isDisplayed"
								label={
									<p>
										<span>会員証登録フォーム項目表示</span>
									</p>
								}
								rules={[
									{
										required: true,
										message: "表示状態を選択してください",
									},
								]}
							>
								<Select placeholder="表示状態を選択してください">
									<Option value="true">表示</Option>
									<Option value="false">非表示</Option>
								</Select>
							</Form.Item>
						</motion.div>
						<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
							<Form.Item
								name="isAdminDisplayed"
								label={
									<p>
										<span>管理画面項目表示</span>
									</p>
								}
								rules={[
									{
										required: true,
										message: "管理画面項目表示",
									},
								]}
							>
								<Select placeholder="表示状態を選択してください">
									<Option value="true">表示</Option>
									<Option value="false">非表示</Option>
								</Select>
							</Form.Item>
						</motion.div>

						<motion.div
							style={{
								marginBottom: "20px",
							}}
							variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
						>
							<FormItem
								name="label"
								label={
									<p>
										<span>項目ラベル</span>
									</p>
								}
								rules={[
									{
										required: true,
										whitespace: true,
										message: "項目ラベルを入力してください",
									},
								]}
							>
								<TextArea
									placeholder={
										currentCustomerRegistration?.customerRegistrationId === 1
											? "例：氏名"
											: currentCustomerRegistration?.customerRegistrationId ===
												  2
												? "例: 電話番号"
												: "例：氏名（漢字）"
									}
									autoSize={{ minRows: 3 }}
									allowClear
								/>
							</FormItem>
							{currentCustomerRegistration &&
								currentCustomerRegistration?.customerRegistrationId === 1 && (
									<p>
										こちらは氏名登録項目ですので、適切なラベルを設定ください。
									</p>
								)}
							{currentCustomerRegistration &&
								currentCustomerRegistration?.customerRegistrationId === 2 && (
									<p>
										こちらは電話番号登録項目ですので、適切なラベルを設定ください。
									</p>
								)}
						</motion.div>
						<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
							<Form.Item name="type" label="" className="mb-0">
								<Radio.Group
									onChange={(e) => {
										setType(e?.target?.value);
									}}
								>
									<Radio
										value="text"
										disabled={
											currentCustomerRegistration &&
											!(currentCustomerRegistration?.type === "text")
										}
									>
										テキスト入力
									</Radio>
									<Radio
										disabled={
											currentCustomerRegistration &&
											!(currentCustomerRegistration?.type === "number")
										}
										value="number"
									>
										数字入力
									</Radio>
									<Radio
										disabled={
											currentCustomerRegistration &&
											!(currentCustomerRegistration?.type === "datepicker")
										}
										value="datepicker"
									>
										日付
									</Radio>
									<Radio
										disabled={
											currentCustomerRegistration &&
											!(currentCustomerRegistration?.type === "checkbox")
										}
										value="checkbox"
									>
										選択
									</Radio>
									<Radio
										disabled={
											currentCustomerRegistration &&
											!(currentCustomerRegistration?.type === "radio")
										}
										value="radio"
									>
										ラジオボタン
									</Radio>
									<Radio
										disabled={
											currentCustomerRegistration &&
											!(currentCustomerRegistration?.type === "image")
										}
										value="image"
									>
										写真
									</Radio>
								</Radio.Group>
							</Form.Item>
						</motion.div>

						{(type === "checkbox" || type === "radio") && (
							<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
								<p>
									<span className="font-bold">選択肢</span>
								</p>
								<p className="text-xs text-gray-400 mb-4 mt-2">
									※項目をドラッグして表示順を自由に調整できます
								</p>
								<Form.List
									name="choices"
									rules={[
										{
											required: true,
											message: "選択肢を入力してください",
										},
									]}
								>
									{(fields, { add, remove }) => (
										<DragDropContext onDragEnd={onChoicesDragEnd}>
											<Droppable
												droppableId="droppableChoices"
												direction="vertical"
											>
												{(provided, snapshot) => (
													<div
														ref={provided.innerRef}
														{...provided.droppableProps}
														className="w-full"
													>
														{fields.map((field, index) => {
															return (
																<Draggable
																	key={field.key}
																	draggableId={field.key + ""}
																	index={index}
																>
																	{(provided, snapshot) => (
																		<div
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			{...provided.dragHandleProps}
																			className="border border-dashed border-gray-300 rounded bg-white p-4 mb-4"
																		>
																			<Form.Item
																				noStyle
																				shouldUpdate={(
																					prevValues,
																					currentValues,
																				) => {
																					return (
																						prevValues?.choices?.length !==
																						currentValues?.choices?.length
																					);
																				}}
																			>
																				{({ getFieldValue }) => {
																					const type = getFieldValue([
																						"choices",
																						index,
																						"type",
																					]);

																					const isDelete = getFieldValue([
																						"choices",
																						index,
																						"isDelete",
																					]);

																					if (type === "other") {
																						return (
																							<div className="flex items-center justify-between">
																								<div>
																									{`選択（${index + 1}）`}
																									{type === "other" ? (
																										<Tag color={colorPrimary}>
																											その他
																										</Tag>
																									) : null}
																								</div>
																								{fields.length > 1 ? (
																									<Button
																										danger
																										onClick={() => {
																											remove(index);
																										}}
																										icon={<CloseOutlined />}
																										disabled={
																											isDelete === false
																										}
																									>
																										選択削除
																									</Button>
																								) : null}
																							</div>
																						);
																					}

																					return (
																						<Form.Item
																							label={
																								<div>
																									{`選択（${index + 1}）`}
																									{type === "other" ? (
																										<Tag color={colorPrimary}>
																											その他
																										</Tag>
																									) : null}
																								</div>
																							}
																						>
																							<Form.Item
																								{...field}
																								key={[field.key, "contents"]}
																								name={[field.name, "contents"]}
																								className="inline-block mr-2"
																								style={{
																									width:
																										fields.length > 1
																											? "calc(80% - 0.25rem)"
																											: "100%",
																								}}
																								rules={[
																									{
																										required: true,
																										message:
																											"選択を入力してください",
																									},
																								]}
																							>
																								<Input
																									placeholder="例：18歳"
																									onPressEnter={(e) =>
																										e.preventDefault()
																									}
																									allowClear
																								/>
																							</Form.Item>
																							{fields.length > 1 ? (
																								<Form.Item
																									{...field}
																									className="inline-block"
																								>
																									<Button
																										danger
																										onClick={() => {
																											remove(index);
																										}}
																										icon={<CloseOutlined />}
																										disabled={
																											isDelete === false
																										}
																									>
																										選択削除
																									</Button>
																								</Form.Item>
																							) : null}
																						</Form.Item>
																					);
																				}}
																			</Form.Item>
																		</div>
																	)}
																</Draggable>
															);
														})}
														{provided.placeholder}
														<Form.Item
															noStyle
															shouldUpdate={(prevValues, currentValues) =>
																prevValues?.choices?.length !==
																currentValues?.choices?.length
															}
														>
															{({ getFieldValue }) => {
																const choices = getFieldValue("choices");
																const hasOther = some(choices, {
																	type: "other",
																});

																if (hasOther) {
																	return (
																		<Button
																			type="link"
																			icon={<PlusOutlined />}
																			block
																			onClick={() =>
																				add({
																					contents: "",
																					type: "normal",
																				})
																			}
																		>
																			選択肢追加
																		</Button>
																	);
																}

																return (
																	<Form.Item>
																		<Dropdown
																			trigger={["click"]}
																			placement="bottomCenter"
																			menu={{
																				items: [
																					{
																						key: "normal",
																						label: "複数選択",
																						onClick: () =>
																							add({
																								contents: "",
																								type: "normal",
																							}),
																					},
																					{
																						key: "other",
																						label: "その他",
																						onClick: () =>
																							add({
																								type: "other",
																								contents:
																									COMMONS.CONTENT_TYPE_SURVEY_OTHER,
																							}),
																					},
																				],
																			}}
																			overlayStyle={{
																				minWidth: "200px",
																				width: "200px",
																			}}
																			arrow
																		>
																			<Button
																				type="link"
																				icon={<PlusOutlined />}
																				block
																			>
																				選択肢追加
																			</Button>
																		</Dropdown>
																	</Form.Item>
																);
															}}
														</Form.Item>
													</div>
												)}
											</Droppable>
										</DragDropContext>
									)}
								</Form.List>
							</motion.div>
						)}
						<Divider />
						<motion.div
							variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
							className="flex justify-center"
						>
							<TapAnimationComponent>
								<Button
									type="primary"
									htmlType="submit"
									size="large"
									className="m-1 w-32"
								>
									追加する
								</Button>
							</TapAnimationComponent>
						</motion.div>
					</motion.div>
				</Form>
			</Modal>
		</>
	);
};

export default CustomerRegistrationModal;
